import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';

/** How long a caller waits for unsaved work before giving up on it. */
const DEFAULT_TIMEOUT_MS = 3_000;

/**
 * Lets whichever page is on screen persist its unsaved work before the session is torn down.
 *
 * Registration IS the opt-in: with nothing registered `saveAll` is a no-op, so no other page is
 * affected. Keyed by owner, mirroring `ColumnOptionsService.registerPersister`, so a stale
 * registration is replaced rather than duplicated.
 */
@Injectable({
  providedIn: 'root',
})
export class UnsavedWorkService {
  private readonly savers = new Map<string, () => Observable<unknown>>();

  private savingAll = false;

  /**
   * True while `saveAll` is in flight.
   *
   * Read by leave guards: the work is already being persisted, so there is nothing left to ask the
   * user, and prompting would stack a dialog on top of a logout that is halfway done.
   */
  get saving(): boolean {
    return this.savingAll;
  }

  registerSaver(owner: string, save: () => Observable<unknown>): void {
    this.savers.set(owner, save);
  }

  unregisterSaver(owner: string): void {
    this.savers.delete(owner);
  }

  /**
   * Best effort, never blocking: a saver that fails, throws, or outlives `timeoutMs` is abandoned and
   * the caller carries on. Losing a draft beats trapping the user midway through logging out.
   */
  saveAll(timeoutMs: number = DEFAULT_TIMEOUT_MS): Observable<unknown> {
    const saves = Array.from(this.savers.values()).map((save) => {
      try {
        return save();
      } catch {
        return of(null);
      }
    });

    if (!saves.length) {
      return of(null);
    }

    this.savingAll = true;

    return forkJoin(saves).pipe(
      timeout(timeoutMs),
      catchError(() => of(null)),
      finalize(() => (this.savingAll = false))
    );
  }
}
